import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ChevronDown, ChevronUp, Copy, Check, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const FutureFeatures = () => {
  const [expandedFeature, setExpandedFeature] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const features = [
    {
      id: 'FF-001',
      title: 'Dark Mode Consistency Validation',
      category: 'UI/UX Enhancement',
      priority: 'Medium',
      estimatedEffort: '2-3 hours',
      description: 'Complete validation and refinement of dark mode across all pages and components to ensure visual consistency, proper contrast ratios, and seamless theme switching.',
      detailedRequirements: [
        'Validate dark mode styling across all pages (Dashboard, Hub, Settings, Reports, etc.)',
        'Test theme toggle functionality on all components',
        'Ensure proper contrast ratios for accessibility (WCAG AA compliance)',
        'Verify dark mode persistence across sessions',
        'Test all modals, dropdowns, and overlays in dark mode',
        'Validate custom components (VehicleCard, ViewModeGrid, etc.) styling',
        'Check icon visibility and colors in dark mode',
        'Test all form inputs and buttons for proper dark mode styling'
      ],
      technicalNotes: [
        'Uses ThemeProvider from providers/ThemeProvider.jsx',
        'Theme toggle implemented using next-themes library',
        'Dark mode classes follow Tailwind CSS dark: prefix convention',
        'System automatically detects user preference via prefers-color-scheme'
      ],
      starterCode: `// Example dark mode styling pattern
// In any component, use Tailwind's dark: prefix:

<div className="bg-white dark:bg-gray-900 text-gray-900 dark:text-white">
  <h1 className="text-orange-500 dark:text-orange-400">Title</h1>
  <Button className="bg-blue-500 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700">
    Click Me
  </Button>
</div>

// For custom components with inline styles:
const enconOrange = '#ee7203';
const bgColor = document.documentElement.classList.contains('dark') 
  ? '#1f2937' // dark gray
  : '#ffffff'; // white

// Testing checklist:
// 1. Toggle dark mode from Settings
// 2. Navigate to each page and verify styling
// 3. Open all modals and dropdowns
// 4. Test all interactive elements (buttons, inputs, cards)
// 5. Verify logo and icon visibility
// 6. Check toast notifications appearance`,
      affectedFiles: [
        '/app/frontend/src/pages/Dashboard.jsx',
        '/app/frontend/src/pages/Hub.jsx',
        '/app/frontend/src/pages/Settings.jsx',
        '/app/frontend/src/pages/Reports.jsx',
        '/app/frontend/src/components/VehicleCard.jsx',
        '/app/frontend/src/components/ViewModeGrid.jsx',
        '/app/frontend/src/components/DashboardDropdown.jsx',
        '/app/frontend/src/App.css',
        '/app/frontend/src/index.css'
      ]
    },
    {
      id: 'FF-002',
      title: 'Sales Dashboard - Full Implementation',
      category: 'New Feature',
      priority: 'High',
      estimatedEffort: '6-8 hours',
      description: 'Develop a comprehensive Sales Dashboard showing key sales metrics, trends, and performance indicators with interactive charts and filtering capabilities.',
      detailedRequirements: [
        'Create sales overview with total revenue, orders, and growth metrics',
        'Implement interactive charts (line, bar, pie) for sales visualization',
        'Add date range selector for custom period analysis',
        'Show top customers and products by sales value',
        'Include sales by vehicle breakdown',
        'Add export functionality for sales reports (CSV, PDF)',
        'Implement real-time data updates from backend',
        'Create filtering options (by date, customer, vehicle, product)',
        'Add monthly/quarterly/yearly comparison views',
        'Show sales target vs. actual performance'
      ],
      technicalNotes: [
        'Placeholder exists at /app/frontend/src/pages/SalesDashboard.jsx',
        'Needs backend API endpoints for sales data aggregation',
        'Consider using Chart.js or Recharts for visualizations',
        'Data should sync with transport log entries',
        'Implement caching for performance with large datasets',
        'Add permission check: user.can_access_sales'
      ],
      starterCode: `// /app/frontend/src/pages/SalesDashboard.jsx
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Download, Calendar, TrendingUp, DollarSign } from 'lucide-react';
import api from '../services/api';
import { API } from '../App';
import { format, subDays } from 'date-fns';

const SalesDashboard = () => {
  const [salesData, setSalesData] = useState(null);
  const [dateRange, setDateRange] = useState({ start: subDays(new Date(), 30), end: new Date() });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchSalesData();
  }, [dateRange]);

  const fetchSalesData = async () => {
    setLoading(true);
    try {
      const response = await api.get(\`\${API}/sales/dashboard\`, {
        params: {
          start_date: format(dateRange.start, 'yyyy-MM-dd'),
          end_date: format(dateRange.end, 'yyyy-MM-dd')
        }
      });
      setSalesData(response.data);
    } catch (error) {
      console.error('Error fetching sales data:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Sales Dashboard</h1>
        <div className="flex gap-3">
          <Button onClick={() => {/* Date picker */}}>
            <Calendar className="w-4 h-4 mr-2" />
            Select Date Range
          </Button>
          <Button onClick={() => {/* Export */}}>
            <Download className="w-4 h-4 mr-2" />
            Export Report
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Total Revenue</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">£{salesData?.totalRevenue || 0}</div>
            <p className="text-xs text-green-600 flex items-center">
              <TrendingUp className="w-3 h-3 mr-1" /> +12.5% from last period
            </p>
          </CardContent>
        </Card>
        {/* Add more KPI cards */}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Sales Trend</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={salesData?.trend || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="sales" stroke="#ee7203" />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        {/* Add more charts */}
      </div>
    </div>
  );
};

export default SalesDashboard;

// Backend API endpoint needed: /api/sales/dashboard
// Expected response format:
// {
//   totalRevenue: number,
//   totalOrders: number,
//   avgOrderValue: number,
//   trend: [{ date: string, sales: number, orders: number }],
//   topCustomers: [{ name: string, revenue: number }],
//   topVehicles: [{ vehicle: string, revenue: number }]
// }`,
      affectedFiles: [
        '/app/frontend/src/pages/SalesDashboard.jsx',
        '/app/backend/server.py (add /api/sales endpoints)'
      ],
      dependencies: ['recharts or chart.js', 'date-fns']
    },
    {
      id: 'FF-003-REMOVED',
      title: 'PI Counts - IMPLEMENTED',
      category: 'Completed',
      priority: 'N/A',
      estimatedEffort: 'N/A',
      description: 'Physical Inventory (PI) Counts system has been implemented. Access via Dashboard → PI Counts card or Settings → Stock Database.',
      detailedRequirements: [
        'Master Stock Database with CSV upload',
        'Product grouping by prefix codes',
        'Supplier group management',
        'Industrial/Structural categorization',
        'Stock valuation upload (GSTV files)',
        'Count creation and management',
        'Stock on Hand view',
        'Discrepancy tracking',
        'Inventory search modal'
      ],
      technicalNotes: [
        'Full implementation at /app/frontend/src/pages/PICounts.jsx',
        'MongoDB collections: master_stock, product_groups, supplier_groups, pi_counts, stock_valuations',
        'Consider mobile-first design for warehouse floor use',
        'Implement offline capability for count entry',
        'Add image upload for proof of count',
        'Integration with transport system for stock movement'
      ],
      starterCode: `// /app/frontend/src/pages/PICounts.jsx
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Search, Plus, Camera, AlertTriangle } from 'lucide-react';
import api from '../services/api';
import { API } from '../App';

const PICounts = () => {
  const [items, setItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCountModal, setShowCountModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [countData, setCountData] = useState({ actual: 0, notes: '' });

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      const response = await api.get(\`\${API}/inventory/items\`);
      setItems(response.data);
    } catch (error) {
      console.error('Error fetching items:', error);
    }
  };

  const submitCount = async () => {
    try {
      await api.post(\`\${API}/inventory/counts\`, {
        item_id: selectedItem.id,
        expected_count: selectedItem.expected_quantity,
        actual_count: countData.actual,
        variance: countData.actual - selectedItem.expected_quantity,
        notes: countData.notes,
        timestamp: new Date().toISOString()
      });
      setShowCountModal(false);
      fetchItems();
    } catch (error) {
      console.error('Error submitting count:', error);
    }
  };

  const filteredItems = items.filter(item =>
    item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.sku.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">PI Counts</h1>
        <Button onClick={() => {/* Add new item */}}>
          <Plus className="w-4 h-4 mr-2" />
          New Item
        </Button>
      </div>

      <div className="flex items-center gap-3">
        <Search className="w-5 h-5 text-gray-500" />
        <Input
          placeholder="Search items by name or SKU..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="flex-1"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredItems.map(item => (
          <Card key={item.id} className="cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => {
              setSelectedItem(item);
              setShowCountModal(true);
            }}
          >
            <CardHeader>
              <CardTitle className="text-lg">{item.name}</CardTitle>
              <p className="text-sm text-gray-500">SKU: {item.sku}</p>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Expected</p>
                  <p className="text-2xl font-bold">{item.expected_quantity}</p>
                </div>
                {item.last_count && (
                  <div className="text-right">
                    <p className="text-sm text-gray-600">Last Count</p>
                    <p className="text-lg font-semibold">{item.last_count.actual}</p>
                    {item.last_count.variance !== 0 && (
                      <AlertTriangle className="w-4 h-4 text-amber-500 inline" />
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Count Entry Modal */}
      <Dialog open={showCountModal} onOpenChange={setShowCountModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Count Entry: {selectedItem?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Expected Quantity</label>
              <Input value={selectedItem?.expected_quantity || 0} disabled />
            </div>
            <div>
              <label className="text-sm font-medium">Actual Count</label>
              <Input
                type="number"
                value={countData.actual}
                onChange={(e) => setCountData({...countData, actual: parseInt(e.target.value)})}
                placeholder="Enter actual count"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Notes</label>
              <Input
                value={countData.notes}
                onChange={(e) => setCountData({...countData, notes: e.target.value})}
                placeholder="Add notes (optional)"
              />
            </div>
            <div className="flex gap-3">
              <Button onClick={submitCount} className="flex-1">Submit Count</Button>
              <Button variant="outline" onClick={() => {/* Camera */}}>
                <Camera className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PICounts;

// Backend endpoints needed:
// GET /api/inventory/items - List all inventory items
// POST /api/inventory/counts - Submit new count
// GET /api/inventory/counts/history/:item_id - Get count history`,
      affectedFiles: [
        '/app/frontend/src/pages/PICounts.jsx',
        '/app/backend/server.py (add /api/inventory endpoints)'
      ],
      dependencies: []
    },
    {
      id: 'FF-004',
      title: 'Goods In - Receiving & Inspection System',
      category: 'New Feature',
      priority: 'Medium',
      estimatedEffort: '6-8 hours',
      description: 'Create a Goods In system for receiving deliveries, quality inspection, and updating inventory with calendar-based tracking.',
      detailedRequirements: [
        'Implement goods receipt entry form',
        'Add supplier management',
        'Create quality inspection checklist',
        'Show daily/weekly goods receipts on calendar',
        'Generate goods received notes (GRN)',
        'Add photo upload for quality issues',
        'Implement barcode scanning for receipt verification',
        'Create approval workflow for discrepant deliveries',
        'Show pending vs. completed receipts',
        'Add integration with purchase orders (if applicable)'
      ],
      technicalNotes: [
        'Placeholder exists at /app/frontend/src/pages/GoodsIn.jsx',
        'Calendar already implemented using FullCalendar',
        'Requires new MongoDB collection: goods_receipts, suppliers',
        'Photo storage using existing GridFS system',
        'Mobile-optimized for warehouse floor use',
        'Consider push notifications for new deliveries'
      ],
      starterCode: `// /app/frontend/src/pages/GoodsIn.jsx (Enhanced)
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import { Plus, Package, Camera, CheckCircle } from 'lucide-react';
import api from '../services/api';
import { API } from '../App';

const GoodsIn = () => {
  const [receipts, setReceipts] = useState([]);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [receiptForm, setReceiptForm] = useState({
    supplier: '',
    reference: '',
    items: [],
    notes: '',
    status: 'pending'
  });

  useEffect(() => {
    fetchReceipts();
  }, []);

  const fetchReceipts = async () => {
    try {
      const response = await api.get(\`\${API}/goods-in/receipts\`);
      setReceipts(response.data);
    } catch (error) {
      console.error('Error fetching receipts:', error);
    }
  };

  const handleDateClick = (info) => {
    setSelectedDate(info.dateStr);
    setShowReceiptModal(true);
  };

  const submitReceipt = async () => {
    try {
      await api.post(\`\${API}/goods-in/receipts\`, {
        ...receiptForm,
        date: selectedDate,
        created_at: new Date().toISOString()
      });
      setShowReceiptModal(false);
      fetchReceipts();
    } catch (error) {
      console.error('Error submitting receipt:', error);
    }
  };

  const calendarEvents = receipts.map(receipt => ({
    title: \`\${receipt.supplier} - \${receipt.items?.length || 0} items\`,
    date: receipt.date,
    backgroundColor: receipt.status === 'completed' ? '#10b981' : '#f59e0b',
    extendedProps: { receipt }
  }));

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Goods In</h1>
        <Button onClick={() => {
          setSelectedDate(new Date().toISOString().split('T')[0]);
          setShowReceiptModal(true);
        }}>
          <Plus className="w-4 h-4 mr-2" />
          New Receipt
        </Button>
      </div>

      {/* Calendar */}
      <Card>
        <CardContent className="pt-6">
          <FullCalendar
            plugins={[dayGridPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            events={calendarEvents}
            dateClick={handleDateClick}
            headerToolbar={{
              left: 'prev,next today',
              center: 'title',
              right: 'dayGridMonth,dayGridWeek'
            }}
          />
        </CardContent>
      </Card>

      {/* Receipt Entry Modal */}
      <Dialog open={showReceiptModal} onOpenChange={setShowReceiptModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>New Goods Receipt - {selectedDate}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Supplier</label>
                <Input
                  value={receiptForm.supplier}
                  onChange={(e) => setReceiptForm({...receiptForm, supplier: e.target.value})}
                  placeholder="Enter supplier name"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Reference Number</label>
                <Input
                  value={receiptForm.reference}
                  onChange={(e) => setReceiptForm({...receiptForm, reference: e.target.value})}
                  placeholder="PO or delivery note #"
                />
              </div>
            </div>
            
            {/* Items Section */}
            <div>
              <label className="text-sm font-medium">Items Received</label>
              <Button variant="outline" size="sm" className="ml-2">
                <Plus className="w-3 h-3 mr-1" />
                Add Item
              </Button>
              {/* Item list here */}
            </div>

            <div>
              <label className="text-sm font-medium">Notes</label>
              <Input
                value={receiptForm.notes}
                onChange={(e) => setReceiptForm({...receiptForm, notes: e.target.value})}
                placeholder="Additional notes"
              />
            </div>

            <div className="flex gap-3">
              <Button onClick={submitReceipt} className="flex-1">
                <CheckCircle className="w-4 h-4 mr-2" />
                Submit Receipt
              </Button>
              <Button variant="outline" onClick={() => {/* Camera */}}>
                <Camera className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GoodsIn;

// Backend endpoints needed:
// GET /api/goods-in/receipts - Get all goods receipts
// POST /api/goods-in/receipts - Create new receipt
// PUT /api/goods-in/receipts/:id - Update receipt status
// GET /api/goods-in/suppliers - Get supplier list`,
      affectedFiles: [
        '/app/frontend/src/pages/GoodsIn.jsx',
        '/app/backend/server.py (add /api/goods-in endpoints)'
      ],
      dependencies: ['@fullcalendar/react', '@fullcalendar/daygrid', '@fullcalendar/interaction']
    },
    {
      id: 'FF-005',
      title: 'Zoho CRM Integration - Quote Sync',
      category: 'Integration',
      priority: 'High',
      estimatedEffort: '8-10 hours',
      description: 'Implement seamless integration with Zoho CRM to automatically sync quotes from the C30 Dashboard to Zoho CRM Quotes module, enabling unified customer relationship management.',
      detailedRequirements: [
        'Set up OAuth 2.0 authentication with Zoho CRM API',
        'Create token management system (access token, refresh token)',
        'Fetch and map Zoho CRM Quotes module field metadata',
        'Implement field mapping between local quotes and Zoho fields',
        'Create quote sync endpoint (create/update)',
        'Add customer lookup/creation in Zoho Accounts module',
        'Implement product line items formatting for Zoho',
        'Add manual sync button on quote pages',
        'Create sync status tracking (synced, pending, failed)',
        'Implement error handling and retry logic',
        'Add sync history and audit trail',
        'Create settings page for Zoho credentials management',
        'Add bi-directional sync option (Zoho → C30)'
      ],
      technicalNotes: [
        'API Domain: https://www.zohoapis.eu (EU datacenter)',
        'Current API Version: v8 (not v2)',
        'OAuth Scopes needed: ZohoCRM.modules.quotes.ALL or ZohoCRM.modules.ALL',
        'Rate Limits vary by plan: Free (5k/day), Standard (25k/day), Professional (50k/day)',
        'Tokens stored securely in MongoDB with encryption',
        'Access tokens expire - implement refresh flow',
        'Duplicate checking on "Subject" field by default',
        'Custom fields in Zoho may vary by organization',
        'API Console: https://api-console.zoho.eu for credential setup',
        'Field API names differ from display labels - use metadata endpoint',
        'Store Zoho record ID in local quote for future updates'
      ],
      starterCode: `// Backend Implementation - /app/backend/server.py

# 1. MongoDB Schema for Zoho Tokens
# Collection: zoho_tokens
# {
#   "client_id": "string",
#   "client_secret": "string (encrypted)",
#   "access_token": "string (encrypted)",
#   "refresh_token": "string (encrypted)",
#   "expires_at": "datetime",
#   "created_at": "datetime",
#   "updated_at": "datetime"
# }

import httpx
from datetime import datetime, timedelta, timezone
from cryptography.fernet import Fernet
import os

# Encryption key for tokens (store in .env)
ZOHO_ENCRYPTION_KEY = os.getenv('ZOHO_ENCRYPTION_KEY', Fernet.generate_key())
cipher = Fernet(ZOHO_ENCRYPTION_KEY)

# Zoho API Configuration
ZOHO_API_BASE_URL = "https://www.zohoapis.eu/crm/v8"
ZOHO_AUTH_URL = "https://accounts.zoho.eu/oauth/v2/token"

# Token Management
async def get_zoho_access_token():
    """Get valid access token, refresh if expired"""
    token_doc = await db.zoho_tokens.find_one({})
    
    if not token_doc:
        raise HTTPException(status_code=400, detail="Zoho not configured. Please set up OAuth credentials in Settings.")
    
    # Check if token is expired
    if datetime.now(timezone.utc) >= token_doc['expires_at']:
        # Refresh token
        return await refresh_zoho_token(token_doc)
    
    # Decrypt and return
    return cipher.decrypt(token_doc['access_token'].encode()).decode()

async def refresh_zoho_token(token_doc):
    """Refresh expired access token"""
    try:
        refresh_token = cipher.decrypt(token_doc['refresh_token'].encode()).decode()
        client_id = token_doc['client_id']
        client_secret = cipher.decrypt(token_doc['client_secret'].encode()).decode()
        
        async with httpx.AsyncClient() as client:
            response = await client.post(
                ZOHO_AUTH_URL,
                params={
                    'refresh_token': refresh_token,
                    'client_id': client_id,
                    'client_secret': client_secret,
                    'grant_type': 'refresh_token'
                }
            )
            
            if response.status_code != 200:
                raise HTTPException(status_code=500, detail="Failed to refresh Zoho token")
            
            data = response.json()
            new_access_token = data['access_token']
            
            # Update in database
            await db.zoho_tokens.update_one(
                {"_id": token_doc['_id']},
                {
                    "$set": {
                        "access_token": cipher.encrypt(new_access_token.encode()).decode(),
                        "expires_at": datetime.now(timezone.utc) + timedelta(seconds=3600),
                        "updated_at": datetime.now(timezone.utc)
                    }
                }
            )
            
            return new_access_token
    except Exception as e:
        logging.error(f"Error refreshing Zoho token: {e}")
        raise HTTPException(status_code=500, detail="Failed to refresh Zoho token")

# Get Zoho Field Metadata (one-time setup)
@api_router.get("/zoho/fields-metadata")
async def get_zoho_fields_metadata(
    current_user: User = Depends(get_current_user)
):
    """Fetch Zoho CRM Quotes module field metadata"""
    if current_user.role != "super_admin":
        raise HTTPException(status_code=403, detail="Admin access required")
    
    try:
        access_token = await get_zoho_access_token()
        
        async with httpx.AsyncClient() as client:
            response = await client.get(
                f"{ZOHO_API_BASE_URL}/settings/fields?module=Quotes",
                headers={"Authorization": f"Zoho-oauthtoken {access_token}"}
            )
            
            if response.status_code != 200:
                raise HTTPException(status_code=500, detail="Failed to fetch Zoho fields")
            
            return response.json()
    except Exception as e:
        logging.error(f"Error fetching Zoho fields: {e}")
        raise HTTPException(status_code=500, detail=str(e))

# Create Quote in Zoho
async def create_zoho_quote(quote_data: dict) -> dict:
    """
    Create a quote in Zoho CRM
    
    Args:
        quote_data: {
            "subject": "Quote #12345",
            "customer_name": "ABC Transport Ltd",
            "contact_name": "John Doe",
            "total_amount": 5000.00,
            "valid_until": "2025-12-31",
            "items": [
                {"product": "Service A", "quantity": 10, "price": 500}
            ],
            "discount": 0,
            "tax": 250,
            "notes": "Additional notes"
        }
    
    Returns:
        dict: Zoho API response with created quote ID
    """
    try:
        access_token = await get_zoho_access_token()
        
        # Map local data to Zoho fields
        zoho_payload = {
            "data": [{
                "Subject": quote_data.get("subject"),
                "Account_Name": quote_data.get("customer_name"),
                "Contact_Name": quote_data.get("contact_name"),
                "Grand_Total": quote_data.get("total_amount"),
                "Valid_Till": quote_data.get("valid_until"),
                "Quote_Stage": "Draft",
                "Discount": quote_data.get("discount", 0),
                "Tax": quote_data.get("tax", 0),
                "Description": quote_data.get("notes", ""),
                "Product_Details": format_zoho_line_items(quote_data.get("items", []))
            }]
        }
        
        async with httpx.AsyncClient() as client:
            response = await client.post(
                f"{ZOHO_API_BASE_URL}/Quotes",
                headers={
                    "Authorization": f"Zoho-oauthtoken {access_token}",
                    "Content-Type": "application/json"
                },
                json=zoho_payload,
                timeout=30.0
            )
            
            if response.status_code not in [200, 201]:
                logging.error(f"Zoho API Error: {response.text}")
                raise HTTPException(status_code=500, detail=f"Zoho API error: {response.text}")
            
            result = response.json()
            return result
    except Exception as e:
        logging.error(f"Error creating Zoho quote: {e}")
        raise HTTPException(status_code=500, detail=str(e))

def format_zoho_line_items(items: list) -> list:
    """Format line items for Zoho Product_Details field"""
    return [
        {
            "product": {
                "name": item.get("product", ""),
                "id": None  # Optional: lookup product ID if exists in Zoho
            },
            "quantity": item.get("quantity", 1),
            "list_price": item.get("price", 0),
            "total": item.get("quantity", 1) * item.get("price", 0)
        }
        for item in items
    ]

# API Endpoint: Create Quote and Sync to Zoho
@api_router.post("/quotes/create-and-sync")
async def create_quote_and_sync_to_zoho(
    quote_data: dict,
    current_user: User = Depends(get_current_user)
):
    """
    Create a quote locally and sync to Zoho CRM
    
    Request Body:
    {
        "subject": "Quote for December Transport",
        "customer_name": "ABC Ltd",
        "contact_name": "John Doe",
        "total_amount": 5000.00,
        "valid_until": "2025-12-31",
        "items": [...],
        "discount": 0,
        "tax": 250,
        "notes": "Special delivery requirements"
    }
    """
    try:
        # 1. Create quote in local database
        local_quote = {
            **quote_data,
            "created_by": current_user.id,
            "created_at": datetime.now(timezone.utc),
            "zoho_sync_status": "pending",
            "zoho_id": None
        }
        
        result = await db.quotes.insert_one(local_quote)
        quote_id = str(result.inserted_id)
        
        # 2. Sync to Zoho CRM
        try:
            zoho_response = await create_zoho_quote(quote_data)
            
            if zoho_response.get("data") and len(zoho_response["data"]) > 0:
                zoho_id = zoho_response["data"][0]["details"]["id"]
                
                # 3. Update local quote with Zoho ID
                await db.quotes.update_one(
                    {"_id": result.inserted_id},
                    {
                        "$set": {
                            "zoho_id": zoho_id,
                            "zoho_sync_status": "synced",
                            "zoho_synced_at": datetime.now(timezone.utc)
                        }
                    }
                )
                
                return {
                    "success": True,
                    "quote_id": quote_id,
                    "zoho_id": zoho_id,
                    "message": "Quote created and synced to Zoho CRM"
                }
            else:
                raise Exception("Invalid Zoho response")
                
        except Exception as sync_error:
            # Mark as failed but keep local quote
            await db.quotes.update_one(
                {"_id": result.inserted_id},
                {
                    "$set": {
                        "zoho_sync_status": "failed",
                        "zoho_sync_error": str(sync_error),
                        "zoho_last_attempt": datetime.now(timezone.utc)
                    }
                }
            )
            
            return {
                "success": False,
                "quote_id": quote_id,
                "message": f"Quote created locally but Zoho sync failed: {str(sync_error)}"
            }
            
    except Exception as e:
        logging.error(f"Error creating quote: {e}")
        raise HTTPException(status_code=500, detail=str(e))

# Update Existing Quote in Zoho
@api_router.put("/quotes/{quote_id}/sync-to-zoho")
async def sync_quote_to_zoho(
    quote_id: str,
    current_user: User = Depends(get_current_user)
):
    """Manually sync an existing quote to Zoho (or update if already synced)"""
    try:
        quote = await db.quotes.find_one({"_id": ObjectId(quote_id)})
        if not quote:
            raise HTTPException(status_code=404, detail="Quote not found")
        
        if quote.get("zoho_id"):
            # Update existing Zoho quote
            access_token = await get_zoho_access_token()
            # Implementation for PUT /Quotes/{zoho_id}
            pass
        else:
            # Create new Zoho quote
            zoho_response = await create_zoho_quote(quote)
            # Update local quote with Zoho ID
            
        return {"success": True, "message": "Quote synced to Zoho"}
    except Exception as e:
        logging.error(f"Error syncing quote: {e}")
        raise HTTPException(status_code=500, detail=str(e))

# Settings Endpoint: Save Zoho OAuth Credentials
@api_router.post("/settings/zoho/credentials")
async def save_zoho_credentials(
    credentials: dict,
    current_user: User = Depends(get_current_user)
):
    """
    Save Zoho OAuth credentials (admin only)
    
    Request Body:
    {
        "client_id": "1000.XXX...",
        "client_secret": "xxx...",
        "code": "authorization_code_from_oauth_flow"
    }
    """
    if current_user.role != "super_admin":
        raise HTTPException(status_code=403, detail="Admin access required")
    
    try:
        # Exchange authorization code for tokens
        async with httpx.AsyncClient() as client:
            response = await client.post(
                ZOHO_AUTH_URL,
                params={
                    'code': credentials['code'],
                    'client_id': credentials['client_id'],
                    'client_secret': credentials['client_secret'],
                    'grant_type': 'authorization_code',
                    'redirect_uri': credentials.get('redirect_uri', 'https://yourdomain.com/oauth/callback')
                }
            )
            
            if response.status_code != 200:
                raise HTTPException(status_code=400, detail="Invalid OAuth code or credentials")
            
            token_data = response.json()
            
            # Encrypt and store tokens
            encrypted_data = {
                "client_id": credentials['client_id'],
                "client_secret": cipher.encrypt(credentials['client_secret'].encode()).decode(),
                "access_token": cipher.encrypt(token_data['access_token'].encode()).decode(),
                "refresh_token": cipher.encrypt(token_data['refresh_token'].encode()).decode(),
                "expires_at": datetime.now(timezone.utc) + timedelta(seconds=token_data.get('expires_in', 3600)),
                "created_at": datetime.now(timezone.utc),
                "updated_at": datetime.now(timezone.utc)
            }
            
            # Upsert (replace existing if any)
            await db.zoho_tokens.replace_one({}, encrypted_data, upsert=True)
            
            return {"success": True, "message": "Zoho credentials saved successfully"}
    except Exception as e:
        logging.error(f"Error saving Zoho credentials: {e}")
        raise HTTPException(status_code=500, detail=str(e))


# Frontend Component Example
// /app/frontend/src/components/ZohoSettings.jsx
import { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { ExternalLink, CheckCircle } from 'lucide-react';
import api from '../services/api';
import { API } from '../App';
import { toast } from 'sonner';

const ZohoSettings = () => {
  const [credentials, setCredentials] = useState({
    client_id: '',
    client_secret: '',
    code: ''
  });

  const handleSave = async () => {
    try {
      const response = await api.post(\`\${API}/settings/zoho/credentials\`, credentials);
      toast.success('Zoho CRM connected successfully!');
    } catch (error) {
      toast.error('Failed to connect Zoho CRM');
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Zoho CRM Integration</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-gray-600">
          Connect your Zoho CRM to automatically sync quotes. 
          <a href="https://api-console.zoho.eu" target="_blank" className="text-orange-500 ml-1">
            Get credentials <ExternalLink className="w-3 h-3 inline" />
          </a>
        </p>
        
        <div>
          <label className="text-sm font-medium">Client ID</label>
          <Input
            value={credentials.client_id}
            onChange={(e) => setCredentials({...credentials, client_id: e.target.value})}
            placeholder="1000.XXX..."
          />
        </div>
        
        <div>
          <label className="text-sm font-medium">Client Secret</label>
          <Input
            type="password"
            value={credentials.client_secret}
            onChange={(e) => setCredentials({...credentials, client_secret: e.target.value})}
            placeholder="Enter secret"
          />
        </div>
        
        <div>
          <label className="text-sm font-medium">Authorization Code</label>
          <Input
            value={credentials.code}
            onChange={(e) => setCredentials({...credentials, code: e.target.value})}
            placeholder="Paste code from OAuth flow"
          />
          <p className="text-xs text-gray-500 mt-1">
            Generate at: https://accounts.zoho.eu/oauth/v2/auth?scope=ZohoCRM.modules.ALL&client_id=YOUR_CLIENT_ID&response_type=code&redirect_uri=YOUR_REDIRECT_URI
          </p>
        </div>
        
        <Button onClick={handleSave} className="w-full">
          <CheckCircle className="w-4 h-4 mr-2" />
          Connect Zoho CRM
        </Button>
      </CardContent>
    </Card>
  );
};

export default ZohoSettings;`,
      affectedFiles: [
        '/app/backend/server.py (add Zoho endpoints)',
        '/app/backend/.env (add ZOHO_ENCRYPTION_KEY)',
        '/app/frontend/src/components/ZohoSettings.jsx (new)',
        '/app/frontend/src/pages/Settings.jsx (integrate ZohoSettings)',
        'MongoDB collections: zoho_tokens, quotes (add zoho_id, zoho_sync_status fields)'
      ],
      dependencies: ['httpx', 'cryptography'],
      referenceLinks: [
        {
          title: 'Zoho CRM API Settings',
          url: 'https://crm.zoho.eu/crm/encon/settings/api/modules',
          description: 'Your Zoho CRM API configuration page - set up OAuth credentials here'
        },
        {
          title: 'Zoho API Console (EU)',
          url: 'https://api-console.zoho.eu',
          description: 'Create Self Client or Server-based OAuth application'
        },
        {
          title: 'Zoho CRM API v8 Documentation',
          url: 'https://www.zoho.com/crm/developer/docs/api/v8/',
          description: 'Official API documentation for Zoho CRM v8'
        },
        {
          title: 'Quotes Module API',
          url: 'https://www.zoho.com/crm/developer/docs/api/v8/insert-records.html',
          description: 'Insert Records API (applicable to Quotes module)'
        },
        {
          title: 'OAuth 2.0 Setup Guide',
          url: 'https://www.zoho.com/crm/developer/docs/api/v8/oauth-overview.html',
          description: 'Complete OAuth 2.0 authentication setup guide'
        }
      ]
    }
  ];

  const copyToClipboard = (id) => {
    navigator.clipboard.writeText(id);
    setCopiedId(id);
    toast.success(`Copied ${id} to clipboard`);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'High': return 'bg-red-100 text-red-800 border-red-300';
      case 'Medium': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'Low': return 'bg-green-100 text-green-800 border-green-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'New Feature': return 'bg-blue-100 text-blue-800';
      case 'UI/UX Enhancement': return 'bg-purple-100 text-purple-800';
      case 'Performance': return 'bg-green-100 text-green-800';
      case 'Integration': return 'bg-indigo-100 text-indigo-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-orange-500">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Future Features</h1>
          <p className="text-gray-600">
            A comprehensive list of planned features and enhancements. Each feature includes a unique ID, detailed requirements, 
            technical notes, and starter code to accelerate development when ready to implement.
          </p>
        </div>

        {/* Feature Cards */}
        <div className="space-y-4">
          {features.map((feature) => (
            <Card key={feature.id} className="overflow-hidden hover:shadow-lg transition-shadow">
              <CardHeader className="bg-gradient-to-r from-orange-50 to-white border-b">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copyToClipboard(feature.id)}
                        className="font-mono text-xs px-2 py-1 h-7"
                      >
                        {copiedId === feature.id ? <Check className="w-3 h-3 mr-1" /> : <Copy className="w-3 h-3 mr-1" />}
                        {feature.id}
                      </Button>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getCategoryColor(feature.category)}`}>
                        {feature.category}
                      </span>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getPriorityColor(feature.priority)}`}>
                        {feature.priority} Priority
                      </span>
                      <span className="text-xs text-gray-500">⏱️ {feature.estimatedEffort}</span>
                    </div>
                    <CardTitle className="text-xl">{feature.title}</CardTitle>
                    <p className="text-sm text-gray-600 mt-2">{feature.description}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setExpandedFeature(expandedFeature === feature.id ? null : feature.id)}
                    className="ml-4"
                  >
                    {expandedFeature === feature.id ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                  </Button>
                </div>
              </CardHeader>

              {expandedFeature === feature.id && (
                <CardContent className="pt-6 space-y-6">
                  {/* Detailed Requirements */}
                  <div>
                    <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                      📋 Detailed Requirements
                    </h3>
                    <ul className="space-y-2">
                      {feature.detailedRequirements.map((req, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                          <span className="text-orange-500 font-bold mt-0.5">•</span>
                          <span>{req}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Technical Notes */}
                  <div>
                    <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                      🔧 Technical Notes
                    </h3>
                    <ul className="space-y-2">
                      {feature.technicalNotes.map((note, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-gray-700 bg-blue-50 p-2 rounded">
                          <span className="text-blue-500 font-bold mt-0.5">→</span>
                          <span>{note}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Affected Files */}
                  <div>
                    <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                      📁 Affected Files
                    </h3>
                    <div className="bg-gray-50 p-3 rounded border">
                      {feature.affectedFiles.map((file, idx) => (
                        <div key={idx} className="font-mono text-xs text-gray-700 py-1">
                          {file}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Dependencies */}
                  {feature.dependencies && feature.dependencies.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                        📦 Dependencies
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {feature.dependencies.map((dep, idx) => (
                          <span key={idx} className="px-3 py-1 bg-purple-100 text-purple-800 rounded text-sm font-mono">
                            {dep}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Starter Code */}
                  <div>
                    <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                      💻 Starter Code
                    </h3>
                    <div className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto">
                      <pre className="text-xs leading-relaxed">
                        <code>{feature.starterCode}</code>
                      </pre>
                    </div>
                  </div>

                  {/* Reference Links */}
                  {feature.referenceLinks && feature.referenceLinks.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                        🔗 Reference Links
                      </h3>
                      <div className="space-y-2">
                        {feature.referenceLinks.map((link, idx) => (
                          <a
                            key={idx}
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-start gap-3 p-3 bg-gradient-to-r from-orange-50 to-white border border-orange-200 rounded-lg hover:shadow-md transition-shadow group"
                          >
                            <div className="flex-shrink-0 w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                              {idx + 1}
                            </div>
                            <div className="flex-1">
                              <div className="font-semibold text-gray-900 group-hover:text-orange-600 flex items-center gap-2">
                                {link.title}
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                </svg>
                              </div>
                              <p className="text-xs text-gray-600 mt-1">{link.description}</p>
                              <p className="text-xs text-gray-400 mt-1 font-mono truncate">{link.url}</p>
                            </div>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              )}
            </Card>
          ))}
        </div>

        {/* Footer */}
        <div className="bg-white p-4 rounded-lg shadow text-center text-sm text-gray-600">
          <p>💡 <strong>Tip:</strong> Click on any Feature ID to copy it to clipboard for easy reference in discussions or task management.</p>
        </div>
      </div>
    </div>
  );
};

export default FutureFeatures;
